/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import ReplyLeftIcon from '@atlaskit/icon-lab/core/reply-left';
import PersonAvatarIcon from '@atlaskit/icon/core/person-avatar';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Anchor, Box, Grid, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Tile from '@atlaskit/tile/tile';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { processExcerpt } from '../../utils/search-result-utils';

import { messages } from './messages';

const styles = cssMap({
	container: {
		// Match the top padding of LinkedResults (Mentioned in) so the visual gap between the
		// section button and its content is consistent across both Comments and Mentioned in.
		// The horizontal indent that aligns the reply icon with the CTA's iconBefore is applied
		// per-row on `commentLink` (12px), mirroring the per-row `paddingLeft` used by
		// LinkedResult — putting it on this container would double-pad the rows.
		paddingTop: token('space.050'),
	},
	commentWrapper: {
		minWidth: '0px',
	},
	commentLink: {
		display: 'block',
		minWidth: '0px',
		textDecoration: 'none',
		color: token('color.text'),
		borderRadius: token('radius.small'),
		// Indent each row by 12px so the reply icon's left edge aligns with the icon inside
		// the "Comments" CTA button above (the CTA Button has 12px = space.150 of internal
		// horizontal padding before its iconBefore). Applied per-row — mirroring the
		// `paddingLeft: space.150` pattern in LinkedResult — so the hover surface stays flush
		// to its row and the Stack parent doesn't double-pad.
		// Vertical gaps between rows come from the parent `<Stack space="space.150">`, matching
		// LinkedResults' `gap: space.150` for Mentioned in entries. The leading gap above the
		// first row comes from `container.paddingTop`. Adding per-row vertical padding here would
		// compound those, leaving Comments visibly taller than Mentioned-in.
		paddingRight: token('space.050'),
		paddingInlineStart: token('space.150'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
	commentLinkAuthorGlyph: {
		display: 'block',
		minWidth: '0px',
		textDecoration: 'none',
		color: token('color.link'),
		borderRadius: token('radius.small'),
		paddingRight: token('space.050'),
		paddingInlineStart: token('space.150'),
		'&:visited': {
			color: token('color.link.visited'),
		},
		'&:hover': {
			textDecoration: 'none',
		},
		'&:focus': {
			textDecoration: 'none',
		},
		'&:active': {
			textDecoration: 'none',
		},
	},
	// Mirror the grid recipe used by LinkedResult (Mentioned in / Related links) so the
	// icon column, the heading row, and the body row all share an identical column-grid
	// rhythm across the three sections. The grid is `auto 1fr` with a 4px column gap.
	commentCardGrid: {
		gridTemplateColumns: 'auto 1fr',
		gridTemplateAreas: `
			'icon heading'
			'icon body'
		`,
		columnGap: token('space.075'),
		rowGap: token('space.025'),
	},
	commentCardGridWithoutHeading: {
		gridTemplateColumns: 'auto 1fr',
		gridTemplateAreas: `'icon body'`,
		columnGap: token('space.075'),
	},
	commentIconArea: {
		gridArea: 'icon',
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'center',
		// Optical-align the icon tile with the heading text baseline (matches LinkedResult.newIcon).
		paddingTop: token('space.025'),
	},
	commentHeadingArea: {
		gridArea: 'heading',
		minWidth: '0px',
	},
	commentBodyArea: {
		gridArea: 'body',
		minWidth: '0px',
	},
	replyCard: {
		paddingInlineStart: token('space.200'),
		borderInlineStartWidth: token('border.width'),
		borderInlineStartStyle: 'solid',
		borderInlineStartColor: token('color.border.discovery'),
	},
	commentBodyWrapper: {
		minWidth: '0px',
	},
	excerptInlineRow: {
		minWidth: '0px',
		overflow: 'hidden',
	},
	excerptIcon: {
		flexShrink: 0,
		display: 'flex',
	},
	excerptText: {
		minWidth: '0px',
		flexGrow: 1,
		flexBasis: '0%',
	},
});

/**
 * Validates that a URL is safe to use as an href (https only).
 * Rejects javascript:, data:, and other non-https schemes.
 */
const isValidHttpsUrl = (url: string | null | undefined): url is string => {
	if (!url || typeof url !== 'string') {
		return false;
	}
	try {
		const parsed = new URL(url);
		return parsed.protocol === 'https:';
	} catch {
		return false;
	}
};

export interface EmbeddedComment {
	id?: string;
	/**
	 * The ID of the parent entity — either the document ID (for top-level comments)
	 * or another comment ID (for threaded replies). Used to sort top-level comments
	 * before replies in the embedded comments list.
	 */
	parentId?: string | null;
	excerpt: string | null;
	url?: string | null;
	displayName?: string | null;
}

/**
 * Maximum number of embedded comment cards rendered per search result.
 *
 * Exported so analytics can report the same cap it renders — see
 * `getEmbeddedCommentsMetadata` in `ui/search-page-relay/search-results/search-results.tsx`.
 * Keep the two in sync.
 */
export const EMBEDDED_COMMENTS_DEFAULT_MAX = 3;

export interface EmbeddedCommentsProps {
	comments: EmbeddedComment[];
	maxComments?: number;
	/**
	 * When adding expand/collapse for "X more comments", use FormattedMessage for i18n.
	 * Example: <FormattedMessage id="embedded-comments.more" defaultMessage="{count} more comments" values={{ count }} />
	 */
}

/**
 * Renders embedded comments that matched a search query within a document result.
 * Shows context text followed by comment excerpt with highlighted matches.
 * Comments are clickable links when URL is available.
 *
 * Note: This component serves a similar purpose to `ThreadBody` used in `search-graph-pull-request`
 * for PR comments. The two components could be consolidated into a single shared comment display
 * component in future (see TREX-1338 / TREX-1337 for context).
 */
export const EmbeddedComments = ({
	comments,
	maxComments = EMBEDDED_COMMENTS_DEFAULT_MAX,
}: EmbeddedCommentsProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (!comments.length) {
		return null;
	}

	// Order top-level comments before threaded replies using parentId.
	// A comment is a reply if its parentId matches another comment's id in the list.
	// This ensures the most contextual comments surface first within the flat list.
	const commentIds = new Set(comments.map((c) => c.id).filter(Boolean));
	const sortedComments = [...comments].sort((a, b) => {
		const aIsReply = a.parentId != null && commentIds.has(a.parentId);
		const bIsReply = b.parentId != null && commentIds.has(b.parentId);
		if (aIsReply === bIsReply) {
			return 0;
		}
		return aIsReply ? 1 : -1;
	});
	const visibleComments = sortedComments.slice(0, maxComments);

	const commentContent = (comment: (typeof visibleComments)[0]) => {
		// noLeadingEllipsis=true prevents processExcerpt from prepending '...' to long excerpts.
		// Leading ellipsis is wrong in a card layout — truncation only happens visually at the end.
		const processedExcerpt = comment.excerpt ? processExcerpt(comment.excerpt, true) : null;
		const isReply = comment.parentId != null && commentIds.has(comment.parentId);

		const hasDisplayName = Boolean(comment.displayName);

		return (
			<Box xcss={isReply ? styles.replyCard : styles.commentWrapper}>
				<Grid xcss={hasDisplayName ? styles.commentCardGrid : styles.commentCardGridWithoutHeading}>
					<Box xcss={styles.commentIconArea}>
						<Tile label="Reply" size="xxsmall">
							<ReplyLeftIcon label="" color="currentColor" />
						</Tile>
					</Box>
					{hasDisplayName ? (
						<Box xcss={styles.commentHeadingArea}>
							{/*
							 * "Re: <thread>" header inherits its colour from the parent anchor
							 * (`commentLink`), which sets `color.link` by default and switches to
							 * `color.link.visited` on `:visited`. The wrapping `<Text color="inherit">`
							 * flows that through so the heading reads as the standard search-result
							 * link colour. The Figma design intentionally omits the `@atlaskit/link`
							 * external-link arrow and hover underline, so the row uses a custom anchor
							 * rather than `<Link>`.
							 *
							 * Prefix the anchor / context text with the localised "Re: " marker so
							 * each row reads naturally ("Re: <thread/topic>") and visually echoes the
							 * reply icon to its left. The `label` is not the user-translatable
							 * displayName itself — it's the matched anchor / context — short and
							 * always rendered alongside the reply affordance.
							 */}
							{fg('rovo_fps_embedded_comments_author_glyph') ? (
								<Text color="inherit" maxLines={1}>
									{formatMessage(messages.commentHeaderPrefix, {
										label: comment.displayName,
									})}
								</Text>
							) : (
								<Text color="color.text.subtle">
									{formatMessage(messages.commentHeaderPrefix, {
										label: comment.displayName,
									})}
								</Text>
							)}
						</Box>
					) : null}
					{processedExcerpt ? (
						<Box xcss={styles.commentBodyArea}>
							<Tooltip content={processedExcerpt}>
								{(tooltipProps) => (
									<Box {...tooltipProps} xcss={styles.commentBodyWrapper}>
										{fg('rovo_fps_embedded_comments_author_glyph') ? (
											<Inline space="space.075" alignBlock="center" xcss={styles.excerptInlineRow}>
												<Box xcss={styles.excerptIcon}>
													<PersonAvatarIcon label="" color={token('color.icon.subtle')} />
												</Box>
												<Box xcss={styles.excerptText}>
													<Text maxLines={1} color="color.text">
														{processedExcerpt}
													</Text>
												</Box>
											</Inline>
										) : (
											<Text maxLines={2} color="color.text">
												{processedExcerpt}
											</Text>
										)}
									</Box>
								)}
							</Tooltip>
						</Box>
					) : null}
				</Grid>
			</Box>
		);
	};

	const commentWrapper = (comment: (typeof visibleComments)[0], index: number) => {
		const key = comment.id || index;
		if (isValidHttpsUrl(comment.url)) {
			return (
				<Anchor
					key={key}
					href={comment.url}
					xcss={
						fg('rovo_fps_embedded_comments_author_glyph')
							? styles.commentLinkAuthorGlyph
							: styles.commentLink
					}
					target="_blank"
					rel="noopener noreferrer"
					onClick={(e) => e.stopPropagation()}
				>
					{commentContent(comment)}
				</Anchor>
			);
		}

		return (
			<Box
				key={key}
				xcss={
					fg('rovo_fps_embedded_comments_author_glyph')
						? styles.commentLinkAuthorGlyph
						: styles.commentLink
				}
			>
				{commentContent(comment)}
			</Box>
		);
	};

	return (
		<Box xcss={styles.container}>
			<Stack space="space.150">{visibleComments.map(commentWrapper)}</Stack>
		</Box>
	);
};
